// =============================================================================
// Session Background Utility
// =============================================================================
// Provides session-based random background image selection.
// Each browser session gets a randomly selected background image that persists
// across page navigations. New sessions (browser restart) get a new random image.
//
// The utility dynamically supports any number of images in the backgrounds folder.
// Simply add/remove images named bg-{n}.png and update TOTAL_IMAGES constant.
// =============================================================================

const STORAGE_KEY = 'transakt_bg_image_index';

/**
 * Total number of background images available.
 * Update this when adding/removing images from public/images/backgrounds/
 * Images should be named: bg-1.png, bg-2.png, ..., bg-{TOTAL_IMAGES}.png
 */
const TOTAL_IMAGES = 3;

/**
 * Gets the background image index for the current session.
 * If no index exists in sessionStorage, generates a random one and stores it.
 *
 * @returns The background image index (1 to TOTAL_IMAGES)
 */
export function getSessionBackgroundIndex(): number {
  // Skip on server-side (SSR safety)
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return 1;
  }

  try {
    // Check if we already have a selection for this session
    const stored = sessionStorage.getItem(STORAGE_KEY);

    if (stored !== null) {
      const index = parseInt(stored, 10);
      // Validate stored index is within current range
      if (!isNaN(index) && index >= 1 && index <= TOTAL_IMAGES) {
        return index;
      }
    }

    // Generate new random index (1 to TOTAL_IMAGES inclusive)
    const newIndex = Math.floor(Math.random() * TOTAL_IMAGES) + 1;
    sessionStorage.setItem(STORAGE_KEY, String(newIndex));

    return newIndex;
  } catch {
    // Fallback if sessionStorage is not available (private browsing, etc.)
    return Math.floor(Math.random() * TOTAL_IMAGES) + 1;
  }
}

/**
 * Gets the URL path for the session's background image.
 *
 * @returns The URL path to the background image (e.g., "/images/backgrounds/bg-3.png")
 */
export function getSessionBackgroundUrl(): string {
  const index = getSessionBackgroundIndex();
  return `/images/backgrounds/bg-${index}.png`;
}

/**
 * Clears the stored background image index.
 * Useful for testing or forcing a new random selection.
 */
export function clearSessionBackground(): void {
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore errors
    }
  }
}
